type HandleMoreListParams = {
  pageSize: number
  page: number
}

export type HandleMoreListReturn<T> = {
  dataSource: Ref<T[]>
  init: (list: T[], params?: HandleMoreListParams) => void
  handleNextPage: () => void
  handlePrevPage: () => void
  onUpdatePageSize: (size: number) => void
  onUpdateCurrentPage: (page: number) => void
  pageCount: ComputedRef<number>
  fullList: Ref<T[]>
  currentPage: Ref<number>
  pageSize: Ref<number>
  onLoadMore: () => void
}

export const useHandleMoreList = <T>(params?: HandleMoreListParams): HandleMoreListReturn<T> => {
  const { pageSize: defaultPageSize = 10, page: defaultPage = 1 } = params || {}
  const pageSize = ref(defaultPageSize)
  const currentPage = ref(defaultPage)
  const dataSource = ref<T[]>([])
  const fullList = ref<T[]>([])

  const init = (list: T[], params?: HandleMoreListParams) => {
    fullList.value = list
    if (params) {
      pageSize.value = params.pageSize
      currentPage.value = params.page
    }
    dataSource.value = list.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
  }

  const handleNextPage = () => {
    currentPage.value++
    dataSource.value = fullList.value.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
  }

  const handlePrevPage = () => {
    currentPage.value--
    dataSource.value = fullList.value.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
  }

  const onUpdatePageSize = (size: number) => {
    currentPage.value = 1
    pageSize.value = size
    dataSource.value = fullList.value.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
  }

  const onUpdateCurrentPage = (page: number) => {
    currentPage.value = page
    dataSource.value = fullList.value.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
  }

  const pageCount = computed(() => {
    return Math.ceil(fullList.value.length / pageSize.value)
  })

  const onLoadMore = () => {
    if (currentPage.value < pageCount.value) {
      currentPage.value++
      dataSource.value = fullList.value.slice(pageSize.value * (currentPage.value - 1), pageSize.value * currentPage.value)
    }
  }

  return {
    dataSource,
    init,
    handleNextPage,
    handlePrevPage,
    currentPage,
    pageSize,
    onUpdatePageSize,
    onUpdateCurrentPage,
    fullList,
    pageCount,
    onLoadMore,
  } as HandleMoreListReturn<T>
}
